import {FeatureFlag} from '../../../@type/features';
import {type WCCartItem} from '../../../@type/woocommerce/cart-item';
import {DefaultCart} from '../reducers/cartReducer';
import {Feature} from '../reducers/environmentReducer';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {formatCostString, formatCurrencyString} from './currency';
import {capitalizeFirstLetter} from './string';
import {getLocaleText} from './translation';

/**
 * Gets the quantity from the cart item
 */
export function cartItemQuantity(cartItem: WCCartItem | null): number {
	return typeof cartItem?.quantity === 'string' ? Number.parseInt(cartItem.quantity) : (cartItem?.quantity ?? 0);
}

export function bundleQuantityLabel(item: WCCartItem) {
	let bundleItemQuantity = '';
	if (item.is_part_of_bundle) {
		const bundle = DefaultCart.contents().find(cartItem => cartItem.item_key === item.bundled_by);
		if (bundle && !isNaN(parseInt(bundle.quantity))) {
			bundleItemQuantity = ` × ${cartItemQuantity(item) / parseInt(bundle.quantity)}`;
		}
	}

	return bundleItemQuantity;
}

export function cartItemLabel(item: WCCartItem) {
	let {name} = item;

	// The store ugoprobaseball.com has an extra options plugin configured
	// in a non-standard way, which is why we need to include the variation
	// in name as it does not come back in the formatted item data.
	if (item.formatted_item_data && item.name_with_variation) {
		name = item.name_with_variation;
	}

	if (!item.is_part_of_bundle) {
		name = '<span>' + name + '</span>';
	}

	const variationTitle = !item.attributes && item.variation_title ? ` - ${item.variation_title}` : '';

	const label = `${name}${variationTitle}`;

	return label;
}

export function cartItemDisplayAmount(item: WCCartItem): string {
	if (item.is_subscription) {
		const stringAmount = item.subscription_price_string?.indexOf(String(item.display_price ?? item.price)) ? formatCostString(Number.parseFloat(item.display_price ?? item.price)) : '';
		return `${MerchantConfiguration.currency.symbol()}${stringAmount}${item.subscription_price_string ?? ''}`;
	}

	if (item.is_bundle) {
		const bundlePrice: number = Number.parseFloat(item.display_price ?? item.price);
		let bundleTotal = bundlePrice * Number.parseFloat(item.quantity);
		const subItems = DefaultCart.contents().filter(cartItem => cartItem.bundled_by === item.item_key);
		subItems.forEach((subItem: WCCartItem) => {
			if (bundlePrice > 0) {
				const subItemPrice: number = Number.parseFloat(subItem.display_price ?? subItem.price);
				bundleTotal += subItemPrice * Number.parseFloat(subItem.quantity);
			}
		});
		return `${formatCurrencyString(bundleTotal)}`;
	}

	if (item.is_part_of_bundle) {
		return `${formatCurrencyString(Number.parseFloat(item.display_price ?? item.price))}/${getLocaleText('each')}`;
	}

	return `${formatCurrencyString(Number.parseFloat(item.display_price ?? item.price) * cartItemQuantity(item))}`;
}

export function cartItemVariationHTML(item: WCCartItem) {
	if (item.formatted_item_data) {
		return `${metaDataRowsHTML(item)}${formattedItemDataHTMLTemplate(item)}`;
	}

	let variationRowHTML = '';

	if (!item.attributes) {
		return variationRowHTML;
	}

	const keys = Object.keys(item.attributes);
	for (const key of keys) {
		const formattedKey = capitalizeFirstLetter(
			key
				.replace('attribute_', '')
				.replace('pa_', '')
				.replace(/-/g, ' '),
		);
		const formattedValue = String(item.attributes[key]).toUpperCase();
		variationRowHTML += `<dt>${formattedKey}: </dt><dd>${formattedValue}</dd>`;
	}

	return `${metaDataRowsHTML(item)}<dl>${variationRowHTML}</dl>`;
}

function metaDataRowsHTML(item: WCCartItem) {
	if (!item.meta_data || Object.entries(item.meta_data).length === 0) {
		return '';
	}

	let html = '';
	for (const meta of item.meta_data) {
		const keyText = capitalizeFirstLetter(meta.key.replace(/_/g, ' '));
		html += `<dt>${keyText}: </dt><dd>${meta.value || '(none)'}</dd>`;
	}

	return `<dl>${html}</dl>`;
}

function formattedItemDataHTMLTemplate(item: WCCartItem) {
	if (!item.formatted_item_data) {
		return '';
	}

	return item.formatted_item_data.replace(/&nbsp;/g, '');
}

type AddToCartOptions = {
	variationId?: number;
	variationAttributes?: Array<[name: string, value: string]>;
};
async function addProductToCart(productId: number, quantity = 1, options?: AddToCartOptions): Promise<boolean> {
	const addToCartURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'add_to_cart_url');

	if (!addToCartURL) {
		return false;
	}

	const formData = new FormData();
	formData.set('add-to-cart', String(productId));
	formData.set('quantity', String(quantity));

	if (options?.variationAttributes) {
		formData.set('product_id', String(productId));
		formData.set('variation_id', String(options?.variationId ?? productId));

		for (const [name, value] of options.variationAttributes) {
			formData.set(`${name}`, value);
		}
	}

	const response = await fetch(addToCartURL, {
		method: 'POST',
		headers: {Accept: 'application/json'},
		body: formData,
	});

	if (!response.ok) {
		return false;
	}

	return true;
}

export {
	addProductToCart,
};
